import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Link } from "react-router-dom";
import { openModal } from "../../store/modal/modalReducer";
import { faTrash, faFileLines, faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function LinkedChats({ current_booking, setIgnoreRefresh, linkedChats, setLinkedChats, setLinkedLoading }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const { current_staff } = useSelector((state) => state.auth);

    // Helper function to get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case "complete":
                return "bg-green-600 text-white";
            case "incomplete":
                return "bg-red-600 text-white";
            default:
                return "bg-gray-500 text-white";
        }
    };

    // Helper function to format status text (replace underscores with spaces)
    const formatStatus = (status) => {
        if (!status) return "";
        return status.replace(/_/g, " ").charAt(0).toUpperCase() + status.replace(/_/g, " ").slice(1);
    };

    async function deleteLink(e, id) {
        e.preventDefault();

        if (window.confirm("Are you sure you want to delete this link?")) {
            await axios({
                method: "delete",
                url: `${process.env.REACT_APP_API_URL}delete_linked_chat/${id}`,
            })
                .then((response) => {
                    axios({
                        method: "get",
                        url: `${process.env.REACT_APP_API_URL}linked_chats/${current_booking.id}`,
                    })
                        .then((response) => {
                            setLinkedChats(response.data);
                            setLoading(false);
                        })
                        .catch((error) => {
                            alert("Failed to get linked chats");
                            setLoading(false);
                        });
                })
                .catch((error) => {
                    alert("Failed to delete link");
                });
        }
    }

    async function linkRespFund(e, id) {
        e.preventDefault();

        await axios({
            method: "delete",
            url: `${process.env.REACT_APP_API_URL}link_resp_fund/${id}`,
        })
            .then((response) => {
                axios({
                    method: "get",
                    url: `${process.env.REACT_APP_API_URL}linked_chats/${current_booking.id}`,
                })
                    .then((response) => {
                        setLinkedChats(response.data);
                        setLoading(false);
                    })
                    .catch((error) => {
                        alert("Failed to get linked chats");
                        setLoading(false);
                    });
            })
            .catch((error) => {
                alert("Failed to create link");
            });
    }

    return (
        <div className="flex flex-col h-full">
            {linkedChats.length > 0 ? (
                <div>
                    {linkedChats.map((chat) => {
                        // Determine which booking to show based on which one is NOT the current booking
                        const linkedBookingData = chat?.booking?.id === current_booking?.id ? chat?.linked_booking : chat?.booking;
                        const linkedUserData = chat?.booking?.id === current_booking?.id ? chat?.linked_user : chat?.user;

                        return (
                            <Link
                                key={chat.id}
                                to={
                                    chat?.booking?.id === current_booking?.id
                                        ? "/booking/" + chat?.linked_booking?.location.toLowerCase().replace(/\s+/g, "") + "?user_id=" + chat?.linked_user_id + "&booking_id=" + chat?.linked_booking_id
                                        : "/booking/" + chat?.booking?.location.toLowerCase().replace(/\s+/g, "") + "?user_id=" + chat?.user_id + "&booking_id=" + chat?.booking_id
                                }
                            >
                                <div className="border-2 w-full rounded px-2 py-2 my-2 cursor-pointer hover:bg-gray-50 relative">
                                    <div className="absolute top-2 right-2 flex flex-col items-end gap-1 z-10">
                                        <div className="flex flex-row items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    dispatch(
                                                        openModal({
                                                            modal_type: "ModalEditLinkedChat",
                                                            modal_props: {
                                                                chat: chat,
                                                                current_booking: current_booking,
                                                                onUpdate: () => setLinkedLoading(true),
                                                            },
                                                        }),
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPencil} className="h-3 w-3 text-blue-500 hover:text-blue-700" />
                                            </button>
                                            <button type="button" onClick={(e) => deleteLink(e, chat.id)}>
                                                <FontAwesomeIcon icon={faTrash} className="h-3 w-3 text-red-500 hover:text-red-700" />
                                            </button>
                                        </div>
                                        {linkedBookingData?.documents_status && (
                                            <FontAwesomeIcon
                                                icon={faFileLines}
                                                className={`h-4 w-4 ${linkedBookingData.documents_status === "missing_required" ? "text-red-500" : "text-green-500"}`}
                                                title={linkedBookingData.documents_status === "missing_required" ? "Missing required documents" : "All documents uploaded"}
                                            />
                                        )}
                                    </div>
                                    <p className="text-sm pr-6">
                                        <span className="font-semibold">
                                            {chat?.booking?.id === current_booking?.id
                                                ? `${chat?.linked_user?.name || ""} ${chat?.linked_user?.surname || ""}`.trim()
                                                : `${chat?.user?.name || ""} ${chat?.user?.surname || ""}`.trim()}
                                        </span>
                                        <br />
                                        {chat?.booking?.id === current_booking?.id ? chat?.linked_booking?.booking_reference : chat?.booking?.booking_reference}
                                        {" | "}
                                        {chat?.user_id == current_booking?.user?.id ? chat?.linked_type : chat?.type}
                                        <br />
                                        {linkedBookingData?.location}
                                        {linkedBookingData?.tag?.name && ` | ${linkedBookingData.tag.name}`}
                                    </p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            ) : (
                <p className="p-2">No linked chats found.</p>
            )}

            {/* Buttons at bottom - both on one line */}
            <div className="mt-3 flex gap-2">
                <button
                    className="bg-primary text-white px-2 py-1 rounded-full text-xs flex-1"
                    onClick={() =>
                        dispatch(
                            openModal({
                                modal_type: "ModalLinkChat",
                                modal_props: {
                                    current_booking: current_booking,
                                    setLinkedLoading: setLinkedLoading,
                                },
                            }),
                        )
                    }
                >
                    Link Chat
                </button>

                <button className="bg-primary text-white px-2 py-1 rounded-full text-xs flex-1" onClick={(e) => linkRespFund(e, current_booking.id)}>
                    Link Resp Fund
                </button>
            </div>
        </div>
    );
}
